import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  approveImportedReceiptDraft,
  getImportedReceiptPoSuggestions,
  getImportedReceipts,
  voidImportedReceipt,
} from '../../services/procurementService';
import { PageHeader } from '../ds';

const extractApiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data;
  if (d?.error) return d.error;
  if (Array.isArray(d?.errors) && d.errors.length) {
    return d.errors.map(x => x.path ? `${x.msg} (${x.path})` : x.msg).join('; ');
  }
  return e?.message || fallback;
};

function ImportedDraftsReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sku, setSku] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [status, setStatus] = useState('');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [suggestionTarget, setSuggestionTarget] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 250 };
      if (sku.trim()) params.sku = sku.trim();
      if (warehouseCode.trim()) params.warehouse_code = warehouseCode.trim().toUpperCase();
      if (status) params.status = status;
      const result = await getImportedReceipts(params);
      setRows(result?.data?.receipts || result?.receipts || []);
    } catch (e) {
      setError(extractApiError(e, 'Failed to load imported receipts'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const openSuggestions = async (row) => {
    setError(''); setSuggestionTarget(row);
    setSuggestions([]);
    setSuggestionsLoading(true);
    setError('');
    try {
      const result = await getImportedReceiptPoSuggestions(row.id);
      setSuggestions(result?.data || []);
    } catch (e) {
      setError(extractApiError(e, 'Failed to load PO suggestions'));
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const openVoidDialog = (row) => {
    setError(''); setVoidTarget(row);
    setVoidReason(`Duplicate/imported receipt cleanup for ${row.grn_number}`);
  };

  const confirmVoid = async () => {
    if (!voidTarget) return;
    setVoiding(true);
    setError('');
    setSuccess('');
    try {
      await voidImportedReceipt(voidTarget.id, voidReason);
      setSuccess(`Deleted imported receipt ${voidTarget.grn_number}`);
      setVoidTarget(null);
      setVoidReason('');
      await loadRows();
    } catch (e) {
      setError(extractApiError(e, 'Failed to delete imported receipt'));
    } finally {
      setVoiding(false);
    }
  };

  const approveFromSuggestion = async (suggestion) => {
    if (!suggestionTarget || !suggestion?.can_approve) return;
    setApproving(true);
    setError('');
    setSuccess('');
    try {
      await approveImportedReceiptDraft(suggestionTarget.id, {
        po_id: suggestion.po_id,
        po_item_id: suggestion.po_item_id,
        quantity: Number(suggestionTarget.quantity || 0)
      });
      setSuccess(`Approved draft ${suggestionTarget.grn_number} against PO ${suggestion.po_number}`);
      setSuggestionTarget(null);
      setSuggestions([]);
      await loadRows();
    } catch (e) {
      setError(extractApiError(e, 'Failed to approve imported receipt draft'));
    } finally {
      setApproving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        primary="Imported Receipts"
        metadata={[{ label: 'Review sheet/import-created receipts and safely delete legacy imported stock records.' }]}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') loadRows(); }}
        />
        <TextField
          size="small"
          label="Warehouse"
          value={warehouseCode}
          onChange={(e) => setWarehouseCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') loadRows(); }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>GRN</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Batch Qty</TableCell>
                <TableCell>Receipt Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No imported receipts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={`${row.id}-${row.item_id || 0}`} hover>
                  <TableCell>{row.grn_number}</TableCell>
                  <TableCell>{row.sku}</TableCell>
                  <TableCell>{row.warehouse_code}</TableCell>
                  <TableCell>{row.batch_number || '-'}</TableCell>
                  <TableCell align="right">{Number(row.quantity || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(row.current_batch_quantity || 0).toLocaleString()}</TableCell>
                  <TableCell>{row.receipt_date}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.inspection_status} color={row.inspection_status === 'approved' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {(row.badges || []).map((badge) => (
                        <Chip key={badge} size="small" variant="outlined" label={badge} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {row.inspection_status === 'draft' && (
                      <Button size="small" startIcon={<LinkIcon />} onClick={() => openSuggestions(row)}>
                        Link
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => openVoidDialog(row)}
                      disabled={row.inspection_status === 'rejected'}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(voidTarget)} onClose={() => setVoidTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete imported receipt</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Typography sx={{ mb: 2 }}>
            This will remove stock impact through the safe receipt-void path and keep the audit trail.
          </Typography>
          {Number(voidTarget?.batch_count || 0) > 0 &&
            Number(voidTarget?.current_batch_quantity || 0) !== Number(voidTarget?.quantity || 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Some of this receipt's batch quantity appears to have already been used. The backend may block deletion if active transfers or reservations depend on it.
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidTarget(null)} disabled={voiding}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmVoid} disabled={voiding || !voidReason.trim()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(suggestionTarget)} onClose={() => setSuggestionTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Open PO suggestions</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {suggestionsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : suggestions.length === 0 ? (
            <Alert severity="info">No open receivable PO line matched this imported receipt.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PO</TableCell>
                  <TableCell>PO Line</TableCell>
                  <TableCell>Fulfillment</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suggestions.map((s) => (
                  <TableRow key={`${s.po_id}-${s.po_item_id}`}>
                    <TableCell>{s.po_number}</TableCell>
                    <TableCell>{s.po_item_id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.fulfillment_type}
                        color={s.fulfillment_type === 'over_receipt_warning' ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right">{Number(s.remaining_quantity || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{s.match_score}</TableCell>
                    <TableCell>{s.reason}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!s.can_approve || approving}
                        onClick={() => approveFromSuggestion(s)}
                      >
                        Approve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestionTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ImportedDraftsReview;
